<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const certificates = [
  {
    id: 'CR-2024-00089',
    project: 'Solar Park Beta – Northern Highlands',
    projectId: 'P-2024-0031',
    issuedDate: '2024-03-15',
    issuedBy: 'Carbon Registry Authority',
    country: 'Kenya',
    sector: 'Renewable Energy',
    creditingPeriodStart: '2024-04-01',
    creditingPeriodEnd: '2029-03-31',
    methodology: 'ACM0002 v19.0',
    estimatedCredits: '12,500 tCO2e/year',
    status: 'Active',
    statusColor: 'green',
  },
]
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-5xl mx-auto px-5">

        <!-- Breadcrumb -->
        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">Registration Certificate</span>
        </nav>

        <!-- Header -->
        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h1 class="text-3xl font-bold text-accent mb-2">Project Registration Certificate</h1>
            <p class="text-gray-600">
              Once approved by the Registry Authority, your project is issued a formal Registration Certificate (CR). This confirms your project is registered under the Article 6.4 mechanism and authorized to commence crediting.
            </p>
          </div>
        </section>

        <!-- Pending notice -->
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
          <i class="fas fa-hourglass-half text-yellow-500 mt-0.5"></i>
          <div>
            <p class="text-sm font-medium text-yellow-800">Mangrove Restoration Initiative – Borneo: Awaiting Registration</p>
            <p class="text-sm text-yellow-700">Your project is currently in the DOE Validation stage. A Registration Certificate will be issued after the government completes their review.</p>
          </div>
        </div>

        <!-- Certificate card -->
        <section class="space-y-4">
          <div
            v-for="cert in certificates"
            :key="cert.id"
            class="bg-white rounded-lg border border-gray-200 overflow-hidden"
          >
            <!-- Certificate banner -->
            <div class="bg-accent px-6 py-4 flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <i class="fas fa-award text-white text-2xl"></i>
                <div>
                  <p class="text-white font-semibold text-lg">Project Registration Certificate</p>
                  <p class="text-white/70 text-sm">Article 6.4 Carbon Registry · Ref: {{ cert.id }}</p>
                </div>
              </div>
              <span class="bg-green-400 text-white px-3 py-1 rounded-full text-sm font-medium">
                {{ cert.status }}
              </span>
            </div>

            <!-- Certificate body -->
            <div class="p-6">
              <h3 class="text-xl font-semibold text-accent mb-6">{{ cert.project }}</h3>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm mb-6">
                <div>
                  <p class="text-gray-500 mb-1">Project ID</p>
                  <p class="font-medium text-gray-800">{{ cert.projectId }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Host Country</p>
                  <p class="font-medium text-gray-800">{{ cert.country }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Sector</p>
                  <p class="font-medium text-gray-800">{{ cert.sector }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Methodology</p>
                  <p class="font-medium text-gray-800">{{ cert.methodology }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Issued Date</p>
                  <p class="font-medium text-gray-800">{{ cert.issuedDate }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Issued By</p>
                  <p class="font-medium text-gray-800">{{ cert.issuedBy }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Crediting Period Start</p>
                  <p class="font-medium text-gray-800">{{ cert.creditingPeriodStart }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Crediting Period End</p>
                  <p class="font-medium text-gray-800">{{ cert.creditingPeriodEnd }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Estimated Annual Credits</p>
                  <p class="font-medium text-gray-800">{{ cert.estimatedCredits }}</p>
                </div>
              </div>

              <div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p class="text-sm text-green-800">
                  <i class="fas fa-check-circle mr-2"></i>
                  This project is formally registered under the Article 6.4 mechanism. You may now proceed to the monitoring and verification phase. Monitoring reports are due annually.
                </p>
              </div>

              <div class="flex items-center space-x-3">
                <button class="px-5 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90 flex items-center">
                  <i class="fas fa-download mr-2"></i>Download Certificate (PDF)
                </button>
                <button class="px-5 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50 flex items-center">
                  <i class="fas fa-share-alt mr-2"></i>Share
                </button>
                <button class="px-5 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50 flex items-center">
                  <i class="fas fa-redo mr-2"></i>Apply for Renewal
                </button>
              </div>
            </div>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>
